import { ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { BreadcrumbsComponent } from 'components/breadcrumbs/breadcrumbs.component';
import { ServiceStepsComponent } from 'components/service/service-steps/service-steps.component';
import { VisitInfoFormComponent } from 'components/service/visit-room/visit-info-form/visit-info-form.component';
import { VisitRoomDocumentFormComponent } from 'components/service/visit-room/visit-room-document-form/visit-room-document-form.component';
import { VisitRoomSummaryComponent } from 'components/service/visit-room/visit-room-summary/visit-room-summary.component';
import { VisitRoomTopicFormComponent } from 'components/service/visit-room/visit-room-topic-form/visit-room-topic-form.component';
import { SpinnerComponent } from 'components/spinner/spinner.component';
import { combineLatest, Observable, Subject } from 'rxjs';
import { ConfirmModalComponent } from 'components/modal/confirm-modal/confirm-modal.component';
import { CanComponentDeactivate } from '../../guards/unsaved-changes.guard';
import { ApiService } from 'services/api.service';
import { ToastService } from 'services/toast.service';
import { Breadcrumb } from 'types/breadcrumb.interface';
import {
  ClientCard,
  ReaderCardByClientId,
  ResearchTopicByClientIdCard,
  ResearchTopicByClientIdCardPagedResponse
} from 'types/client.interface';
import { ReadingRoomListItem, ReadingRoomListItemPagedResponse } from 'types/reading-room.interface';
import { BackofficeReadingRoomSubmissionCard, UpdateDraftReadingRoomSubmissionRequest } from 'types/service.interface';
import { VisitRoomSubmissionServiceSteps } from 'types/visit-room.interface';
@Component({
  selector: 'app-visit-room-service-page',
  standalone: true,
  imports: [
    ServiceStepsComponent,
    VisitInfoFormComponent,
    BreadcrumbsComponent,
    TranslateModule,
    SpinnerComponent,
    VisitInfoFormComponent,
    VisitRoomTopicFormComponent,
    VisitRoomDocumentFormComponent,
    VisitRoomSummaryComponent,
    ConfirmModalComponent
  ],
  templateUrl: './visit-room-service-page.component.html',
  styleUrl: './visit-room-service-page.component.scss'
})
export class VisitRoomServicePageComponent implements OnInit, CanComponentDeactivate {
  isOpenLeaveConfirm: boolean = false;
  private leaveConfirm$?: Subject<boolean>;
  public readonly new: string = VisitRoomSubmissionServiceSteps.New.toString();
  public readonly created: string = VisitRoomSubmissionServiceSteps.Created.toString();
  public readonly PreparedResearchTopics: string = VisitRoomSubmissionServiceSteps.PreparedResearchTopics.toString();
  public readonly PreparedRequestedDocuments: string = VisitRoomSubmissionServiceSteps.PreparedRequestedDocuments.toString();
  steps: string[] = [
    VisitRoomSubmissionServiceSteps.New.toString(),
    VisitRoomSubmissionServiceSteps.Created.toString(),
    VisitRoomSubmissionServiceSteps.PreparedResearchTopics.toString(),
    VisitRoomSubmissionServiceSteps.PreparedRequestedDocuments.toString()
  ];
  breadcrumbs: Breadcrumb[] = [
    { title: 'components.breadcrumbs.workspace', url: '/lasitavas-apmeklejums' },
    {
      title: 'components.breadcrumbs.visitRoomForm',
      url: '/pakalpojumi/lasitavas-ampeklejums'
    }
  ];
  currentStep: string = VisitRoomSubmissionServiceSteps.New.toString();
  stepIndex: number = 1;
  readingRooms: ReadingRoomListItem[] = [];
  readersCard?: ReaderCardByClientId;
  researchTopics: ResearchTopicByClientIdCard[] = [];
  clientId: string = '';
  client?: ClientCard;
  isReadingRoomsLoading: boolean = true;
  isReadersCardsLoading: boolean = true;
  isClientLoading: boolean = true;
  isApplicationLoading: boolean = true;
  isResearchTopicsLoading: boolean = true;
  applicationId?: string;
  application?: BackofficeReadingRoomSubmissionCard;
  initialReadingRoomId?: number;
  frontofficeStatus: string = 'New';
  hasFormChanged: boolean = false;
  isSubmitted: boolean = false;
  get isLoading(): boolean {
    return this.isReadingRoomsLoading || this.isResearchTopicsLoading || this.isApplicationLoading;
  }

  constructor(
    private apiService: ApiService,
    private route: ActivatedRoute,
    private toastService: ToastService,
    private cdRef: ChangeDetectorRef,
    private router: Router
  ) {}

  ngOnInit(): void {
    window.scrollTo(0, 0);

    combineLatest([this.route.params, this.route.queryParams]).subscribe(([params, queryParams]) => {
      this.applicationId = params['submissionId'];
      if (this.applicationId && !this.application) {
        this.apiService.getReadingRoomSubmission(this.applicationId).subscribe((data: BackofficeReadingRoomSubmissionCard) => {
          this.application = data;
          this.isSubmitted = data.frontofficeStatus !== 'Draft' && data.frontofficeStatus !== 'Unknown';
          this.frontofficeStatus = data?.frontofficeStatus;
          this.isApplicationLoading = false;
          this.clientId = this.application?.client.id || '';

          if (this.application.initializationStatus === 'InitializationDone') {
            this.router.navigate([`/pieteikums/${this.application.id}`]);
          }

          switch (data.initializationStatus) {
            case 'Created':
              this.stepIndex = 2;
              break;
            case 'PreparedResearchTopics':
              this.stepIndex = 3;
              break;
            case 'PreparedRequestedDocuments':
              this.stepIndex = 4;
              break;
            default:
              this.stepIndex = 1;
          }

          if (queryParams['solis']) {
            this.stepIndex = parseInt(queryParams['solis']);
          } else {
            this.currentStep = this.getStepByIndex(this.stepIndex);
          }

          this.getClientData();
        });
      } else {
        this.isApplicationLoading = false;
      }

      if (params['clientId']) {
        this.clientId = params['clientId'];
        this.getClientData();
      }

      if (queryParams['lasitava']) {
        this.initialReadingRoomId = parseInt(queryParams['lasitava']);
      }

      if (queryParams['solis']) {
        this.stepIndex = parseInt(queryParams['solis']);
      }

      this.currentStep = this.getStepByIndex(this.stepIndex);
    });

    this.apiService.getReadingRoomList().subscribe(
      (data: ReadingRoomListItemPagedResponse) => {
        this.readingRooms = data.list ?? [];
        this.isReadingRoomsLoading = false;
      },
      (error: any) => {
        console.error(error);
        this.isReadingRoomsLoading = false;
      }
    );
  }

  getClientData(): void {
    this.apiService.getReadersCard(this.clientId).subscribe(
      (data: ReaderCardByClientId) => {
        this.readersCard = data;
      },
      (error) => {
        console.error(error);
      }
    );

    this.apiService.getClient(this.clientId).subscribe(
      (data: ClientCard) => {
        this.client = data;
        this.isClientLoading = false;
      },
      (error) => {
        console.error(error);
        this.isClientLoading = false;
      }
    );

    this.apiService.getClientResearchTopics(this.clientId).subscribe(
      (data: ResearchTopicByClientIdCardPagedResponse) => {
        this.researchTopics = data.list || [];
        this.isResearchTopicsLoading = false;
      },
      (error: any) => {
        console.error(error);
        this.isResearchTopicsLoading = false;
      }
    );
  }

  handleApplicationUpdated(application: BackofficeReadingRoomSubmissionCard): void {
    this.application = application;
    this.frontofficeStatus = application.frontofficeStatus!;
    this.cdRef.detectChanges();
  }

  handleSaveDraft(args: UpdateDraftReadingRoomSubmissionRequest): void {
    this.apiService.updateVisitRoomInfoDraft(args).subscribe(
      (data: BackofficeReadingRoomSubmissionCard) => {
        this.application = data;
        this.toastService.showSuccess('draftSaved', '', {
          shouldTranslate: true
        });
      },
      (error: any) => {
        console.error(error);
        this.toastService.showRequestError();
      }
    );
  }

  getStepByIndex(index: number): string {
    return this.steps[index - 1];
  }

  handleFormChanged(hasFormChanged: boolean): void {
    this.hasFormChanged = hasFormChanged;
  }

  canDeactivate(): boolean | Observable<boolean> {
    if (!this.hasFormChanged) return true;
    this.leaveConfirm$ = new Subject<boolean>();
    this.isOpenLeaveConfirm = true;
    return this.leaveConfirm$.asObservable();
  }

  onLeaveConfirm(): void {
    this.isOpenLeaveConfirm = false;
    this.hasFormChanged = false;
    this.leaveConfirm$?.next(true);
    this.leaveConfirm$?.complete();
  }

  onLeaveCancel(): void {
    this.isOpenLeaveConfirm = false;
    this.leaveConfirm$?.next(false);
    this.leaveConfirm$?.complete();
  }
}
